import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardStepper,
} from '@/components/wizards';
import { useGetSubscription } from '@/hooks/useSubscription';
import type { RecipeWizardData } from '@/sections/asset/components/tabs/credentials/RecipeWizard/RecipeWizard.types';
import {
  InputsStep as RecipeInputsStep,
  OverviewStep as RecipeOverviewStep,
  RequestStep as RecipeRequestStep,
  ReviewStep as RecipeReviewStep,
  SessionStep as RecipeSessionStep,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/steps';
import { RecorderHost } from '@/sections/asset/components/tabs/credentials/RecorderProgress';
import { SeedTypeOption } from '@/types';

import type {
  AddSeedByTypeModalProps,
  SeedWizardFormData,
} from './AddSeedByTypeModal.types';
import {
  useAddSeedSubmit,
  useAuthenticationConfigurationAccess,
} from './hooks';
import {
  buildRecorderLeadingSteps,
  CredentialApproachStep,
  CredentialAssignStep,
  CredentialFormStep,
  CredentialStep,
  DataEntryStep,
  IntroStep,
  SubmitProgressStep,
  TypeSelectionStep,
} from './steps';
import { parseSeedCount, parseSeedLines } from './steps/credentialAssign.util';
import {
  DEFAULT_SEED_FORM_DATA,
  isWebSeed,
  seedWizardSteps,
  STEP_IDS,
} from './wizardConfig';

/**
 * AddSeedByTypeModal - A wizard-based dialog for adding seeds by type
 *
 * Simplified to only handle text input - file imports are now handled
 * via the ImportSeedsModal flow.
 *
 * ## Architecture Decisions
 * - Hosted in a shadcn `Dialog` (`max-w-3xl`) for exact Hannibal parity; the
 *   public `AddSeedByTypeModalProps` contract is unchanged so all consumers
 *   render it the same way (`open` / `onClose` / `onSuccess` / `onOpenImport?`).
 * - Wizard handles navigation, skip, and form data (seedType, textInput, credential)
 * - A `WizardStepper` (dots) header shows progress + the current step title;
 *   `currentStep` is position-derived from `progress.currentDisplayStep` (the
 *   step index), never from the validation-aware completion ratio.
 * - File imports delegated to ImportSeedsModal (opened via onOpenImport)
 * - canSubmit validation checks for non-empty text input
 * - Subscription validation intercepts handleNext to block locked features
 * - On submit, the body switches to a progress view (seeds → credentials)
 *   and the footer collapses to a single Close button.
 */
export const AddSeedByTypeModal = ({
  open,
  onClose,
  onSuccess,
  onOpenImport,
}: AddSeedByTypeModalProps) => {
  const { data: subscription } = useGetSubscription();
  const { canConfigureAuthentication, authenticationDisabledTooltip } =
    useAuthenticationConfigurationAccess();
  const isWebApplicationsLocked =
    !subscription?.attackSurface?.applications ||
    subscription.numberOfApplications === 0;

  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );

  const wizard = useWizard<SeedWizardFormData>({
    steps: seedWizardSteps,
    initialData: DEFAULT_SEED_FORM_DATA,
  });

  const {
    handleSubmit,
    isPending,
    isComplete,
    seedsStatus,
    credentialsStatus,
    credentialResults,
    error,
    recorderResults,
    reset: resetSubmit,
  } = useAddSeedSubmit({
    formData: wizard.formData,
    onSuccess,
  });

  const handleClose = () => {
    wizard.reset();
    resetSubmit();
    setSubscriptionError(null);
    onClose();
  };

  const handleOpenImport = () => {
    if (onOpenImport) {
      handleClose();
      onOpenImport();
    }
  };

  // Single consolidated reset effect on modal open
  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (open) {
      wizard.reset();
      const timer = setTimeout(() => {
        setSubscriptionError(null);
        resetSubmit();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Clear text when seed type changes
  useEffect(() => {
    wizard.setFormData(prev => ({ ...prev, textInput: '' }));
  }, [wizard.formData.seedType]);

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const nextText = e.target.value;
    wizard.setFormData(prev => {
      const next = { ...prev, textInput: nextText };
      // F2: the single-vs-multi credential branch is derived from seed count in
      // every step's shouldSkip. credential.mode is NOT re-derived, so crossing
      // the >1 boundary can strand a 'configure-multi' bundle on the single-seed
      // path (or a single 'configure' bundle on the multi path). When the boundary
      // flips relative to the stored mode, reset to Skip — honest + visible (the
      // CredentialStep radio shows Skip again) and avoids bundle-remap ambiguity.
      const isMultiNow = isWebSeed(next) && parseSeedCount(nextText) > 1;
      if (prev.credential.mode === 'configure-multi' && !isMultiNow) {
        next.credential = { mode: 'skip' };
      } else if (prev.credential.mode === 'configure' && isMultiNow) {
        next.credential = { mode: 'skip' };
      }
      return next;
    });
  };

  const handleNextWithSubscriptionCheck = () => {
    const { seedType } = wizard.formData;

    if (
      (seedType === SeedTypeOption.WEB_APPLICATION ||
        seedType === SeedTypeOption.WEB_SERVICE) &&
      isWebApplicationsLocked
    ) {
      setSubscriptionError(
        'Please contact your MSP to enable Web testing in your subscription'
      );
      return;
    }

    setSubscriptionError(null);
    wizard.navigation.handleNext();
  };

  // Steps the user actually sees, in order — drives the stepper header and the
  // position→stepId mapping for back-nav. Mirrors useWizard's internal derivation.
  const visibleSteps = useMemo(
    () =>
      seedWizardSteps
        .filter(step => !step.shouldSkip?.(wizard.formData))
        .sort((a, b) => a.order - b.order),
    [wizard.formData]
  );

  const canSubmit = useMemo(() => {
    const hasText = wizard.formData.textInput.trim().length > 0;
    return hasText && !isPending;
  }, [wizard.formData.textInput, isPending]);

  // Intro and type-selection advance on the wizard's own step validity; the
  // remaining steps gate Next/Finish on having entered seed text.
  const isPreEntryStep =
    wizard.currentStepId === STEP_IDS.INTRO ||
    wizard.currentStepId === STEP_IDS.SELECT_TYPE;
  const isCredentialChoiceBlocked =
    wizard.currentStepId === STEP_IDS.CREDENTIAL &&
    wizard.formData.credential.mode === 'configure' &&
    !canConfigureAuthentication;

  const footerConfig = useWizardFooterConfig({
    // INTRO/SELECT_TYPE advance on the wizard's own step validity. Every other
    // step requires seed text (canSubmit) AND the current step's own validate().
    // For non-recipe steps validate()=>true, so `&& canGoNext` is a no-op there;
    // for the recipe steps it enforces the reused Overview/Inputs/Request/Session
    // validators. On RECIPE_REVIEW validate()=>true, so the final submit gates
    // only on canSubmit (hasText && !isPending) — correct.
    canGoNext:
      (isPreEntryStep
        ? wizard.navigation.canGoNext
        : canSubmit && wizard.navigation.canGoNext) &&
      !isCredentialChoiceBlocked,
    canGoBack: wizard.navigation.canGoBack,
    isPending,
    isLastStep: wizard.navigation.isLastStep,
    onNext: handleNextWithSubscriptionCheck,
    onBack: wizard.navigation.handleBack,
    onCancel: handleClose,
    onSubmit: handleSubmit,
    labels: { submit: 'Add Seeds', submitting: 'Adding...' },
  });

  // Once the user clicks Add Seeds, swap the body for the progress view and
  // the footer for a single Close button. The progress view stays mounted
  // until the modal is closed (so the user sees the final checkmarks).
  const isSubmitView = isPending || isComplete;

  // Stepper stays mounted in the submit view so the wizard chrome never
  // disappears. When any recorder is active, append ONE terminal "Recording" dot
  // (a phase indicator, not one per identity); otherwise the last real step
  // simply stays active during the brief submit.
  const hasRecorders = recorderResults.length > 0;
  const stepperSteps = useMemo(() => {
    const base = visibleSteps.map(step => ({
      id: step.id,
      title: step.title,
    }));
    if (isSubmitView && hasRecorders) {
      return [...base, { id: 'recording', title: 'Recording' }];
    }
    return base;
  }, [visibleSteps, isSubmitView, hasRecorders]);

  // 1-based active index: last dot while submitting, else the wizard's position.
  const stepperCurrentStep = isSubmitView
    ? stepperSteps.length
    : wizard.progress.currentDisplayStep;

  // Bridge the inline recipe steps to recipeDraft inside formData.credential.data
  // (no new useState — secrets stay in formData so reset() clears them, P2-2).
  // null outside configure mode; the recipe-step shouldSkip gates the arms to
  // configure+recipe so the arms only render when this is non-null.
  const recipeBridge = (() => {
    if (wizard.formData.credential.mode !== 'configure') return null;
    const draft = wizard.formData.credential.data.recipeDraft;
    const firstSeedUrl = parseSeedLines(wizard.formData.textInput)[0] ?? '';
    const setDraft: Dispatch<SetStateAction<RecipeWizardData>> = action => {
      wizard.setFormData(prev => {
        if (prev.credential.mode !== 'configure') return prev;
        const next =
          typeof action === 'function'
            ? (action as (p: RecipeWizardData) => RecipeWizardData)(
                prev.credential.data.recipeDraft
              )
            : action;
        return {
          ...prev,
          credential: {
            mode: 'configure',
            data: { ...prev.credential.data, recipeDraft: next },
          },
        };
      });
    };
    return { draft, setDraft, firstSeedUrl };
  })();

  const renderCurrentStep = () => {
    if (isSubmitView) {
      if (hasRecorders) {
        // Recorder flow: fold the two global submit steps INTO each recorder's
        // scrolling table (as leading rows) so they share the recorder rows'
        // green check-circle styling instead of floating above the table. The
        // two global rows are REPEATED at the top of every recorder table (per
        // the multi-identity decision). React Compiler is on — compute inline,
        // no useMemo.
        const leadingSteps = buildRecorderLeadingSteps({
          seedType: wizard.formData.seedType,
          seedsStatus,
          credentialsStatus,
        });
        return (
          <>
            {/* SubmitProgressStep is kept ONLY for its per-identity failure
                list: an identity that fails to spawn a recorder has no
                conversationId, so it gets no RecorderHost row below — the
                failure list is its only surfacing. hideGlobalSteps suppresses
                the two global rows here since they now live inside each recorder
                table via leadingSteps. */}
            <SubmitProgressStep
              seedType={wizard.formData.seedType}
              seedsStatus={seedsStatus}
              credentialsStatus={credentialsStatus}
              credentialResults={credentialResults}
              hideGlobalSteps
            />
            {/* STACKED per-identity recorder progress: one RecorderHost row per
                identity that spawned a conversation, each watching its own
                recorder agent and owning its own close affordance (mirror of
                Host A's AuthenticationTab progress view). */}
            <div className="space-y-6">
              {recorderResults.map(result => (
                <RecorderHost
                  key={result.id}
                  conversationId={result.conversationId}
                  label={result.label}
                  closePolicy="always-enabled"
                  onClose={handleClose}
                  leadingSteps={leadingSteps}
                />
              ))}
            </div>
          </>
        );
      }

      // Non-recorder flow (static credential / skip): no recorder table exists,
      // so the two global steps render in the standalone SubmitProgressStep
      // exactly as before.
      return (
        <SubmitProgressStep
          seedType={wizard.formData.seedType}
          seedsStatus={seedsStatus}
          credentialsStatus={credentialsStatus}
          credentialResults={credentialResults}
        />
      );
    }

    const commonProps = {
      formData: wizard.formData,
      setFormData: wizard.setFormData,
      isSubmitting: isPending,
    };

    switch (wizard.currentStepId) {
      case STEP_IDS.INTRO:
        return <IntroStep />;

      case STEP_IDS.SELECT_TYPE:
        return (
          <TypeSelectionStep
            {...commonProps}
            subscriptionError={subscriptionError}
          />
        );

      case STEP_IDS.ADD_SEEDS:
        return (
          <DataEntryStep
            {...commonProps}
            onTextChange={handleTextChange}
            onOpenImport={handleOpenImport}
          />
        );

      case STEP_IDS.CREDENTIAL:
        return <CredentialStep {...commonProps} />;

      case STEP_IDS.CREDENTIAL_APPROACH:
        return <CredentialApproachStep {...commonProps} />;

      case STEP_IDS.CREDENTIAL_FORM:
        return <CredentialFormStep {...commonProps} />;

      case STEP_IDS.CREDENTIAL_ASSIGN:
        return <CredentialAssignStep {...commonProps} />;

      // Recipe sub-steps render bare here — the parent WizardStepper +
      // WizardModalFooter provide the chrome (one stepper, one footer). No
      // nested RecipeWizard, no nested footer/progress bar. shouldSkip gates
      // these to configure+recipe, so recipeBridge is non-null when reached.
      case STEP_IDS.RECIPE_OVERVIEW:
        return (
          recipeBridge && (
            <RecipeOverviewStep
              data={recipeBridge.draft}
              setData={recipeBridge.setDraft}
              webAppUrl={recipeBridge.firstSeedUrl}
            />
          )
        );

      case STEP_IDS.RECIPE_INPUTS:
        return (
          recipeBridge && (
            <RecipeInputsStep
              data={recipeBridge.draft}
              setData={recipeBridge.setDraft}
            />
          )
        );

      case STEP_IDS.RECIPE_REQUEST:
        return (
          recipeBridge && (
            <RecipeRequestStep
              data={recipeBridge.draft}
              setData={recipeBridge.setDraft}
            />
          )
        );

      case STEP_IDS.RECIPE_SESSION:
        return (
          recipeBridge && (
            <RecipeSessionStep
              data={recipeBridge.draft}
              setData={recipeBridge.setDraft}
            />
          )
        );

      case STEP_IDS.RECIPE_REVIEW:
        return (
          recipeBridge && (
            <RecipeReviewStep
              data={recipeBridge.draft}
              webAppUrl={recipeBridge.firstSeedUrl}
            />
          )
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="flex h-[700px] max-h-[85vh] max-w-3xl flex-col gap-4 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Add Seeds</DialogTitle>
          <DialogDescription className="sr-only">
            Add seeds by type and optionally configure credentials.
          </DialogDescription>
        </DialogHeader>

        <WizardStepper
          className="shrink-0"
          variant="dots"
          steps={stepperSteps}
          currentStep={stepperCurrentStep}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          {renderCurrentStep()}

          {isSubmitView && error && (
            <div
              role="alert"
              aria-live="assertive"
              className="border-chariot-stroke bg-chariot-background-secondary mt-2 flex flex-col gap-2 rounded-md border p-3 pl-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="text-error text-xs">{error.message}</div>
              {/* Retry re-invokes handleSubmit, which the hook re-runs against
                  ONLY the failed (seed, identity) pairs via its persisted
                  succeededIdentityKeys set (useAddSeedSubmit.ts:598-607). This
                  is the sole in-place recovery path — the alternative,
                  close→reopen, resets that set (handleClose → resetSubmit →
                  setSucceededIdentityKeys(new Set())) and re-creates duplicate
                  credentials, which is the bug this button fixes. Rendered once
                  the run has settled (isComplete && !isPending) so it covers
                  BOTH failure shapes: the non-recorder Close footer AND the
                  recorder case where the modal footer is hidden. */}
              {isComplete && !isPending && (
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={isPending}
                  variant="default"
                  size="sm"
                  className="shrink-0 self-end sm:self-auto"
                  data-testid="wizard-retry-button"
                >
                  Retry
                </Button>
              )}
            </div>
          )}
        </div>

        {isSubmitView ? (
          // When recorders are present, each RecorderHost row renders its own
          // close affordance ("Stop watching"/"Close"), so the modal-level
          // footer is redundant and hidden (mirror of Host A). For non-recorder
          // submits (static types) the plain Close button stays.
          hasRecorders ? null : (
            <SubmitDoneFooter isComplete={isComplete} onClose={handleClose} />
          )
        ) : (
          <WizardModalFooter
            config={footerConfig}
            primaryDisabledTooltip={
              isCredentialChoiceBlocked
                ? authenticationDisabledTooltip
                : undefined
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface SubmitDoneFooterProps {
  isComplete: boolean;
  onClose: () => void;
}

/**
 * Plain "Close" footer for the NON-recorder submit view (static credential
 * types + credential-skip). Enabled only once the add completes. The
 * recorder-aware "Stop watching"/"Close" toggle moved into each RecorderHost row
 * (COMPLEX_LOGIN), so this footer is hidden entirely when recorders are present.
 */
function SubmitDoneFooter({ isComplete, onClose }: SubmitDoneFooterProps) {
  return (
    <div className="mt-6 flex shrink-0 items-center justify-end pt-6 pb-5">
      <Button
        onClick={onClose}
        disabled={!isComplete}
        variant="default"
        size="lg"
        className="min-w-24 px-4"
        data-testid="wizard-close-button"
      >
        Close
      </Button>
    </div>
  );
}
